import { useParams } from 'react-router-dom';
import { useGetRestaurantIdentityQuery } from '@/app/api/hubApi';
import { isTransportError } from '@/app/api/loadResilience';
import ThemeProvider from '@/features/restaurant/ThemeProvider';
import ConnectionLostNotice from '@/components/chrome/ConnectionLostNotice';
import GlobalLoader from '@/components/ui/GlobalLoader';
import ErrorToast from '@/components/ui/ErrorToast';
import ResetPasswordPage from './ResetPasswordPage';

/**
 * DF-0297 / DF-0298 — la page « définir mon mot de passe » a sa propre coque,
 * sœur de `serveur/*`, au lieu d'être une route fille de `RestaurantShell`.
 *
 * Deux raisons, toutes deux mesurées le 2026-09-22 sur des enseignes réelles :
 *
 * 1. `RestaurantShell` ferme la page derrière les **portes commerciales** du
 *    restaurant — identité introuvable, module de commande éteint, licence
 *    expirée. Or hub répond 404 sur `/public/<uuid>/` dès que le module est
 *    éteint : sur une enseigne dans cet état, un jeton parfaitement valide
 *    ouvrait « 404 — veuillez accéder via le lien de votre restaurant », zéro
 *    champ mot de passe. 182 enseignes sur 206 ont le module éteint, et le
 *    scénario normal d'une reprise de clientèle est d'importer les clients
 *    PUIS d'allumer le module le jour de la bascule : les liens mouraient
 *    pendant leur fenêtre de 3 jours. Cette page n'ouvre aucun droit de
 *    commande — elle n'a besoin que de l'uuid et du jeton.
 *
 * 2. `RestaurantShell` monte aussi toute la garniture commerciale, dont la
 *    fenêtre d'accueil (`AnnouncementModal`), qui se posait par-dessus le
 *    formulaire et **avalait le clic** sur « Enregistrer » (30 s de
 *    `subtree intercepts pointer events`). C'est la première seconde d'un
 *    parcours de récupération de compte.
 *
 * L'identité n'est donc lue QUE pour le thème, et son échec ne ferme jamais la
 * page : sans elle, le formulaire s'affiche aux couleurs par défaut. Seule une
 * panne de transport garde son propre écran, même règle que le reste du site
 * (DF-0257) — là le réseau est en cause, pas le lien.
 */
export default function ResetPasswordShell() {
  const { restaurantUuid = '' } = useParams<{ restaurantUuid: string }>();
  const {
    data: identity,
    isLoading,
    error,
    refetch,
  } = useGetRestaurantIdentityQuery(restaurantUuid, { skip: !restaurantUuid });

  if (isLoading) return <GlobalLoader />;
  if (isTransportError(error)) return <ConnectionLostNotice onRetry={refetch} />;

  const form = (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <main className="flex-1">
        <ResetPasswordPage />
      </main>
      <ErrorToast />
    </div>
  );

  // Pas d'identite (module eteint, uuid inconnu) : le formulaire s'affiche
  // quand meme, aux couleurs par defaut. C'est tout l'objet de DF-0297.
  return identity ? <ThemeProvider theme={identity.theme}>{form}</ThemeProvider> : form;
}
